import { Fragment } from "react";
import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import set from "lodash/set";
import has from "lodash/has";
import Ajv from "ajv";
import addFormats from "ajv-formats";
import { getStore } from "@store";
import getRouterConfig from "@hooks/useRouter";
import { formatErrors } from "@features/sypi/_utils/sypi-interface-helpers";
import { isEmptyObject } from "@utils/object-helpers";
import { SYPI_ERROR_TYPES } from "@constants";
import {
  setToastMessage,
  setAlertDock,
  setError,
  setTabErrors,
  clearErrors,
} from "@actions/global";

/**
 * SyPI error routing + schema validation.
 *
 * `sendFinalReport` is the single point of egress for the {type,
 * title, message, redirect} objects returned by scheduler / import / delete
 * flows; it fans out to setToastMessage or setAlertDock based on the report
 * type.
 *
 * `sendGlobalError`, `dispatchErrors`, `getSchemaError`,
 * `attemptRevolvingSchemaErrors` handle AJV validation and translate the
 * AJV error shape into the sypiTab / alertDock UX.
 */

export const sendFinalReport = (report, store = getStore()) => {
  switch (report.type) {
    case "TOAST_MESSAGE":
      const { history } = getRouterConfig();
      store.dispatch(
        setToastMessage(
          report.message,
          report.redirect && history ? history.push(report.redirect) : null
        )
      );
      break;
    case "ALERT_DOCK":
      store.dispatch(
        setAlertDock({
          title: report.title,
          message: report.message,
        })
      );
    default:
      break;
  }
};

export const sendGlobalError = (
  configSchemaError,
  configErrors,
  release_version,
  store = getStore()
) => {
  const { activeReleaseVersion, initialReleaseVersion } =
    store.getState().sypiSchema;
  const releaseVersion = release_version
    ? release_version
    : initialReleaseVersion;

  if (
    configSchemaError &&
    !isEmptyObject(configSchemaError) &&
    releaseVersion === activeReleaseVersion
  ) {
    store.dispatch(setError(configSchemaError));
    store.dispatch(
      setAlertDock({
        title: "Schema Config Sync Error",
        message: (
          <Fragment>
            The following errors in the configuration file {releaseVersion} have
            a conflict with schema validation: <br />
            {Object.values(configSchemaError).map((error) => (
              <b key={error.fullPath}>
                {`${error.customMessage}.`} <br />
              </b>
            ))}
            Please sync the configuration file {release_version} with the SyPI
            schema by navigating to Import and selecting 'Update Config with
            Schema Defaults'.
          </Fragment>
        ),
      })
    );
  } else {
    store.dispatch(setError({}));
    store.dispatch(clearErrors());
  }
  if (configErrors.length) {
    store.dispatch(setTabErrors(formatErrors(configErrors)));
  } else {
    store.dispatch(setTabErrors({}));
  }
};

// Runs AJV validation over configToValidate against configSchema and routes
// results to sendGlobalError for UI display. Returns the raw error report so
// callers (updateSchemaConfigDefaults, saveScheduleWithSchemaDefaults) can
// branch on it.
export const dispatchErrors = (
  configToValidate,
  configSchema,
  release_version = "",
  store = getStore()
) => {
  if (
    !Object.keys(configToValidate).some(
      (key) => Object.keys(configToValidate[key]).length === 0
    ) ||
    !store
  ) {
    const entireValidation = new Ajv({
      allErrors: true,
      multipleOfPrecision: 8,
    });
    addFormats(entireValidation);
    const copyConfigToValidate = cloneDeep(configToValidate);
    const validatedWhole = entireValidation.validate(
      configSchema,
      copyConfigToValidate
    );
    let fullErrorReport = {
      schemaErrors: [],
      configMissingKeys: [],
      hasMinLenght: [],
      otherErrors: [],
    };
    let configSchemaError = {},
      configValidatedErrors = [];
    if (!validatedWhole) {
      const { errors } = entireValidation;
      for (let schemaError in errors) {
        let path = errors[schemaError].instancePath
            .substring(1)
            .replace(/\//g, "."),
          subNestedPath;
        if (
          errors[schemaError].keyword ===
            SYPI_ERROR_TYPES.ADDITIONAL_PROPERTIES ||
          errors[schemaError].keyword === SYPI_ERROR_TYPES.TYPE
        ) {
          configSchemaError = {
            ...configSchemaError,
            ...getSchemaError(errors[schemaError]),
          };
        } else if (
          errors[schemaError].keyword === SYPI_ERROR_TYPES.MINIMUM_LENGTH
        ) {
          configValidatedErrors.push(path);
          fullErrorReport.hasMinLenght.push(
            `${errors[schemaError].message} ${path}`
          );
        } else {
          subNestedPath = Object.values(errors[schemaError].params)[0];
          let finalMessage = `${subNestedPath} ${errors[schemaError].message} ${path}.${subNestedPath}`;
          configValidatedErrors.push(`${path}.${subNestedPath}`);
          if (errors[schemaError].keyword === SYPI_ERROR_TYPES.REQUIRED) {
            fullErrorReport.configMissingKeys.push(finalMessage);
          } else {
            fullErrorReport.otherErrors.push(finalMessage);
          }
        }
      }
      fullErrorReport.schemaErrors = Object.keys(configSchemaError).map(
        (schemaError) => configSchemaError[schemaError].customMessage
      );
    }

    if (store) {
      sendGlobalError(
        configSchemaError,
        configValidatedErrors,
        release_version
      );
    }
    return { fullErrorReport, configSchemaError };
  }
};

export const getSchemaError = (schemaError) => {
  let subNestedPath = "";
  const path = schemaError.instancePath.substring(1).replace(/\//g, ".");
  const pathKeys = path.split(".");

  if (schemaError.keyword === "additionalProperties") {
    subNestedPath = Object.values(schemaError.params)[0];
  }

  const correctPath = pathKeys.includes(subNestedPath)
    ? pathKeys.filter((pathPortion) => pathPortion !== subNestedPath).join(".")
    : path;
  const fullPath = subNestedPath
    ? `${correctPath}.${subNestedPath}`
    : correctPath;
  const configSchemaError = {
    [fullPath]: {
      ...schemaError,
      propertyKey: subNestedPath,
      customMessage: `${subNestedPath} ${[
        schemaError.message,
      ]} in ${correctPath}`,
      fullPath: fullPath,
    },
  };

  return configSchemaError;
};

export const attemptRevolvingSchemaErrors = (
  pathConfigChanges,
  sypiClientConfig,
  masterConfigTemplate,
  configSchemaError = {},
  defaultToMaster = true
) => {
  const sypiClientConfigCopy = cloneDeep(sypiClientConfig);
  const masterConfigTemplateCopy = cloneDeep(masterConfigTemplate);

  const updatedConfig = pathConfigChanges?.length
    ? pathConfigChanges.reduce((sypiConfigACC, configPath) => {
        if (
          (!configSchemaError[configPath] ||
            configSchemaError[configPath]?.additionalProperties) &&
          has(sypiConfigACC, configPath) &&
          has(sypiClientConfigCopy, configPath)
        ) {
          const liveValue = get(sypiClientConfigCopy, configPath);
          set(sypiConfigACC, configPath, liveValue);
        }
        return sypiConfigACC;
      }, masterConfigTemplateCopy)
    : defaultToMaster
    ? masterConfigTemplateCopy
    : sypiClientConfigCopy;

  return updatedConfig;
};

export const scheduledConfigError = (scheduledErrors, store = getStore()) => {
  store.dispatch(
    setAlertDock({
      title: `Config changes failed to go live`,
      message: (
        <Fragment>
          Errors were found in the following configuration file(s):
          {scheduledErrors.map((errorName) => (
            <b key={errorName}>
              {`${errorName}.`} <br />
              <br />
            </b>
          ))}
          Please fix them and reschedule the change.
        </Fragment>
      ),
    })
  );
};
